import React, { RefObject, useEffect, useRef, useState } from 'react';
import { format, isSameDay, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Message } from '../../types';
import MessageBubble from './MessageBubble.tsx';

type ConversationMessagesProps = {
  messages: Message[];
  messagesEndRef: RefObject<HTMLDivElement>;
  onImprove: (message: Message) => void;
  onTeachResponse: (message: Message) => void;
  unreadCount?: number;
  unreadSeparatorMarginClassName?: string;
  messageContainerClassName?: string;
};

const getMessageDate = (timestamp: string): Date => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const getDateLabel = (date: Date): string => {
  if (isToday(date)) return 'Hoje';
  if (isYesterday(date)) return 'Ontem';
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
};

const getFirstUnreadMessageId = (messages: Message[], unreadCount: number): string | null => {
  let remaining = unreadCount;
  let firstUnreadId: string | null = null;

  for (let index = messages.length - 1; index >= 0 && remaining > 0; index -= 1) {
    if (messages[index].sender === 'client') {
      firstUnreadId = messages[index].id;
      remaining -= 1;
    }
  }

  return firstUnreadId;
};

export default function ConversationMessages({
  messages,
  messagesEndRef,
  onImprove,
  onTeachResponse,
  unreadCount = 0,
  unreadSeparatorMarginClassName = '',
  messageContainerClassName = '',
}: ConversationMessagesProps) {
  const unreadCountAtOpen = useRef(unreadCount);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<string | null>(null);

  useEffect(() => {
    if (firstUnreadMessageId || unreadCountAtOpen.current <= 0 || !messages.length) return;
    setFirstUnreadMessageId(getFirstUnreadMessageId(messages, unreadCountAtOpen.current));
  }, [firstUnreadMessageId, messages]);

  return (
    <>
      {messages.map((message, index) => {
        const date = getMessageDate(message.timestamp);
        const previousMessage = messages[index - 1];
        const showDate = !previousMessage || !isSameDay(date, getMessageDate(previousMessage.timestamp));
        const isFirstUnread = message.id === firstUnreadMessageId;
        const compactSpacing = Boolean(previousMessage && previousMessage.sender === message.sender && !showDate && !isFirstUnread);

        return (
          <React.Fragment key={message.id}>
            {showDate && (
              <div className="flex justify-center py-2">
                <span className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-600 shadow-sm">
                  {getDateLabel(date)}
                </span>
              </div>
            )}
            {isFirstUnread && (
              <div className={`flex justify-center bg-black/5 py-3 ${unreadSeparatorMarginClassName}`}>
                <span className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm">
                  {unreadCountAtOpen.current} {unreadCountAtOpen.current === 1 ? 'mensagem não lida' : 'mensagens não lidas'}
                </span>
              </div>
            )}
            <MessageBubble
              message={message}
              onImprove={onImprove}
              onTeachResponse={onTeachResponse}
              compactSpacing={compactSpacing}
              containerClassName={messageContainerClassName}
            />
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </>
  );
}
